import { useEffect, useRef, useState } from 'react'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../../constants/common'
import { EVENT_NAME_ONLINE } from '../../../../constants/event'
import { isBrowserOffline } from '../../../../utils/common'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import {
  getIsGretaAIChatFeedTurnInFlight,
  getIsGretaAIChatIdle,
  getIsGretaAIChatOffline,
  getIsGretaAIChatRetryableStatus,
  getIsGretaAIChatThinking,
} from '../../GretaAIChat.utils'
import { appendGretaAIChatUserMessage } from '../../GretaAIChatMessage'
import { useGretaAIChatChatCreation } from '../useGretaAIChatChatCreation'
import { useGretaAIChatGenerationPoll } from '../useGretaAIChatGenerationPoll'
import { useGretaAIChatGenerationWait } from '../useGretaAIChatGenerationWait'
import { useGretaAIChatHistory } from '../useGretaAIChatHistory'
import { useGretaAIChatReplyRequest } from '../useGretaAIChatReplyRequest'
import { useGretaAIChatReplySettle } from '../useGretaAIChatReplySettle'
import { useGretaAIChatTimedOutTurns } from '../useGretaAIChatTimedOutTurns'
import { useGretaAIChatTurnStartedAt } from '../useGretaAIChatTurnStartedAt'

import {
  getIsGretaAIChatLastTurnUser,
  withGretaAIChatRetryFlag,
} from './useGretaAIChatConversation.utils'

export function useGretaAIChatConversation({
  activeChatId = null,
  getIsGenerationSettledRecently = EMPTY_FUNCTION,
  onChatCreated = EMPTY_FUNCTION,
  onChatMissing = EMPTY_FUNCTION,
  onGenerationSettled = EMPTY_FUNCTION,
  onTurnSettled = EMPTY_FUNCTION,
  onTurnStart = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const [messages, setMessages] = useState(EMPTY_LIST)
  const [status, setStatus] = useState(GretaAIChatStatus.Idle)

  const loadedChatIdRef = useRef(null)
  const turnControllersRef = useRef(new Map())

  // Lets async code check the latest chat instead of a stale closure.
  const activeChatIdRef = useRef(activeChatId)

  useEffect(() => {
    activeChatIdRef.current = activeChatId
  }, [activeChatId])

  function getIsOpenConversation(conversationId) {
    return conversationId === activeChatIdRef.current
  }

  function getIsNewChatViewActive() {
    return activeChatIdRef.current === null
  }

  const { clearTurn, preserveOrStartTurn, startTurn, turnStartedAt } =
    useGretaAIChatTurnStartedAt({
      activeChatId,
      isThinking: getIsGretaAIChatThinking(status),
    })

  const {
    checkGenerationSettled,
    getIsPolling,
    pollUntilSettled,
    stopPolling,
  } = useGretaAIChatGenerationPoll()

  const {
    clearTimedOutTurn,
    getIsTimeoutStopPending,
    markTurnTimedOut,
    settleTimeoutStop,
    syncTimedOutTurn,
  } = useGretaAIChatTimedOutTurns()

  const {
    endReplySettle,
    getIsReplySettling,
    isReplySettling,
    startReplySettle,
  } = useGretaAIChatReplySettle({ activeChatId, getIsOpenConversation })

  function getIsTurnInFlight(conversationId) {
    return (
      turnControllersRef.current.has(conversationId) ||
      getIsPolling(conversationId)
    )
  }

  function getIsTurnAwaitingReply(conversationId) {
    return (
      getIsTurnInFlight(conversationId) && !getIsReplySettling(conversationId)
    )
  }

  const {
    abortReloadMessages,
    fetchMessages,
    forgetDeliveredTurn,
    loadOlderMessages,
    reloadMessages,
    syncDeliveredTurn,
    trackDeliveredTurn,
  } = useGretaAIChatHistory({
    activeChatId,
    getIsTimeoutStopPending,
    getIsTurnInFlight: getIsTurnAwaitingReply,
    loadedChatIdRef,
    setMessages,
    setStatus,
    syncTimedOutTurn,
    onChatMissing,
  })

  const {
    cancelQueuedTurn,
    cancelServerTurn,
    clearPendingAnswerLabel,
    enterGenerationWait,
    getIsStopPending,
    pendingAnswerFileName,
    settleGenerationWait,
    trackBackgroundGeneration,
    waitForPendingStop,
  } = useGretaAIChatGenerationWait({
    abortReloadMessages,
    activeChatId,
    clearTurn,
    getIsGenerationSettledRecently,
    getIsOpenConversation,
    getIsTimeoutStopPending,
    getIsTurnInFlight,
    pollUntilSettled,
    preserveOrStartTurn,
    reloadMessages,
    setStatus,
    settleTimeoutStop,
    turnControllersRef,
    onChatMissing,
    onGenerationSettled,
    onTurnSettled,
  })

  useEffect(() => {
    function handleOnline() {
      if (getIsGretaAIChatOffline(status)) {
        if (messages.length === 0) {
          reloadMessages()
        } else {
          setStatus(GretaAIChatStatus.Idle)
        }
      }
    }

    window.addEventListener(EVENT_NAME_ONLINE, handleOnline)

    return () => window.removeEventListener(EVENT_NAME_ONLINE, handleOnline)
  }, [messages, reloadMessages, status])

  const { requestReply } = useGretaAIChatReplyRequest({
    cancelServerTurn,
    checkGenerationSettled,
    clearTurn,
    endReplySettle,
    enterGenerationWait,
    fetchMessages,
    forgetDeliveredTurn,
    getIsOpenConversation,
    getIsStopPending,
    markTurnTimedOut,
    reloadMessages,
    setMessages,
    setStatus,
    settleGenerationWait,
    startReplySettle,
    startTurn,
    syncDeliveredTurn,
    trackDeliveredTurn,
    turnControllersRef,
    onGenerationSettled,
    onTurnSettled,
    onTurnStart,
  })

  const { abortChatCreation, createChatAndSend } = useGretaAIChatChatCreation({
    getIsNewChatViewActive,
    loadedChatIdRef,
    requestReply,
    setMessages,
    setStatus,
    onChatCreated,
  })

  async function dispatchTurn(turnMessages) {
    // Check offline before sending: avoids a slow generic failure and, in a new
    // chat, an empty conversation the user never asked for.
    if (isBrowserOffline()) {
      setStatus(GretaAIChatStatus.Offline)

      return
    }

    setStatus(GretaAIChatStatus.Thinking)
    clearTimedOutTurn(activeChatId)

    const shouldSend = await waitForPendingStop(activeChatId)

    if (!shouldSend) {
      if (getIsOpenConversation(activeChatId)) {
        await reloadMessages(activeChatId)
      }
    } else if (activeChatId) {
      await requestReply(activeChatId, turnMessages)
    } else {
      await createChatAndSend(turnMessages)
    }
  }

  function getIsActiveChatTurnBlocked() {
    return getIsGretaAIChatFeedTurnInFlight(activeChatId, {
      getIsTimeoutStopPending,
      getIsTurnInFlight,
    })
  }

  function sendMessage(text) {
    const trimmedText = text.trim()

    if (
      trimmedText.length > 0 &&
      !getIsGretaAIChatThinking(status) &&
      !getIsActiveChatTurnBlocked()
    ) {
      const nextMessages = appendGretaAIChatUserMessage(messages, trimmedText)

      setMessages(nextMessages)
      dispatchTurn(nextMessages)

      return true
    }

    return false
  }

  function stopGeneration() {
    if (getIsGretaAIChatThinking(status) && !cancelQueuedTurn(activeChatId)) {
      const controller = turnControllersRef.current.get(activeChatId)

      controller?.abort()

      if (activeChatId === null) {
        abortChatCreation()
        setStatus(GretaAIChatStatus.Idle)
      } else {
        // Stays Thinking until the server confirms the stop — Idle here would let retry race it.
        cancelServerTurn(activeChatId, controller)
      }
    }
  }

  const canRetry =
    getIsGretaAIChatLastTurnUser(messages) &&
    (getIsGretaAIChatIdle(status) || getIsGretaAIChatRetryableStatus(status))

  const retryFlaggedMessages = canRetry
    ? withGretaAIChatRetryFlag(messages)
    : messages

  function retryLastReply() {
    if (!getIsActiveChatTurnBlocked()) {
      dispatchTurn(messages)
    }
  }

  function forgetChat(conversationId) {
    turnControllersRef.current.get(conversationId)?.abort()
    turnControllersRef.current.delete(conversationId)
    clearTimedOutTurn(conversationId)
    settleTimeoutStop(conversationId)
    clearTurn(conversationId)
    clearPendingAnswerLabel(conversationId)
    stopPolling(conversationId)
  }

  return {
    forgetChat,
    isReplySettling,
    loadOlderMessages,
    messages: retryFlaggedMessages,
    pendingAnswerFileName,
    reloadMessages,
    retryLastReply,
    sendMessage,
    status,
    stopGeneration,
    trackBackgroundGeneration,
    turnStartedAt,
  }
}
